/**
 * WerkstattCrossLinkCard — Issue #329 (Epic 4 · Sub 2)
 * ----------------------------------------------------
 * Cross-link UI shown inside the Gutachten-Akten-Detail view to surface (and
 * manage) the link between a Gutachten-Akte and its corresponding Werkstatt-
 * Akte at LCS GmbH.
 *
 * Three rendering modes — all driven purely by props so the card stays
 * decoupled from the still-pending DB schema in #328:
 *
 *   1. LINKED       — `werkstattAkteId` is set: render a header chip
 *                     "Werkstatt: LCS / AK-xxx" that deep-links to the
 *                     Werkstatt-Akte detail page.
 *   2. SUGGESTION   — `werkstattAutoMatchKandidaten` is non-empty AND no link
 *                     exists yet: render the auto-match banner with a
 *                     "Verknüpfen" button per candidate.
 *   3. UNLINKED     — neither of the above: render the two action buttons
 *                     "+ Mit bestehender Werkstatt-Akte verknüpfen" (picker)
 *                     and "+ Neue Werkstatt-Akte anlegen" (Epic 3 Sub 7).
 *
 * Modes 1 and 2 can co-exist (a link plus extra unconfirmed candidates).
 * If everything is empty AND no callbacks are supplied, the component
 * short-circuits to `null` so it never leaves an empty white box behind in
 * the detail layout.
 *
 * Backend coupling
 * ----------------
 * The columns `akten.werkstatt_akte_id` and `akten.werkstatt_auto_match_kandidaten`
 * landed with #328. Both fields are now typed in the `Akte` interface (Issue #723)
 * so they are accessed directly without type casts.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { ExternalLink, Loader2, Wrench, Plus, Link2, FileText, Receipt } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';
import {
  berechneErstattungsAufschluesselung,
  formatErstattungsAufschluesselung,
} from '@/lib/auslage-berechnung';
import type { WerkstattAutoMatchKandidat } from '@/lib/database.types';
export type { WerkstattAutoMatchKandidat } from '@/lib/database.types';

interface LinkedWerkstattAkte {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  kunde_name: string | null;
  kennzeichen: string | null;
}

export interface WerkstattCrossLinkCardProps {
  /** Set when the Gutachten-Akte is already linked to a Werkstatt-Akte. */
  werkstattAkteId?: string | null;
  /** Auto-match candidates produced by the backend RPC (#328). */
  werkstattAutoMatchKandidaten?: WerkstattAutoMatchKandidat[] | null;
  /** Opens the picker to choose an existing Werkstatt-Akte to link. */
  onLinkExisting?: () => void;
  /** Triggers Epic 3 Sub 7 — opens the Werkstatt-Akte creation flow. */
  onCreateNew?: () => void;
  /**
   * Called when the user confirms one of the auto-match candidates. The
   * caller is responsible for persisting the link via the backend.
   */
  onConfirmKandidat?: (kandidat: WerkstattAutoMatchKandidat) => void;
  /**
   * Gutachten-Honorar (netto) aus der SV-Büro-Rechnung — für die
   * Erstattungs-Aufschlüsselung (Issue #584).
   */
  gutachtenHonorarNetto?: number | null;
}

interface WerkstattRechnungData {
  auslage_netto: number | null;
  abrechnungsschreiben_dokument_id: string | null;
  abrechnungsschreiben_url: string | null;
}

export default function WerkstattCrossLinkCard({
  werkstattAkteId,
  werkstattAutoMatchKandidaten,
  onLinkExisting,
  onCreateNew,
  onConfirmKandidat,
  gutachtenHonorarNetto,
}: WerkstattCrossLinkCardProps) {
  const [linked, setLinked] = useState<LinkedWerkstattAkte | null>(null);
  const [loading, setLoading] = useState(false);
  const [rechnungData, setRechnungData] = useState<WerkstattRechnungData | null>(null);
  const [, navigate] = useLocation();
  // Same useRef-guard pattern as LinkedAkteBanner — avoid an effect/setState
  // loop when the parent re-creates its `akte` object on every realtime tick.
  const lastFetchedId = useRef<string | null>(null);

  useEffect(() => {
    const id = werkstattAkteId || null;
    if (lastFetchedId.current === id) return;
    lastFetchedId.current = id;

    if (!id) {
      setLinked(null);
      setRechnungData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [akteResult, rechnungResult] = await Promise.all([
          supabase
            .from('akten')
            .select('id, akten_nummer, akten_nr, kunde_name, kennzeichen')
            .eq('id', id)
            .maybeSingle(),
          supabase
            .from('rechnungen')
            .select('netto_betrag, abrechnungsschreiben_dokument_id')
            .eq('akte_id', id)
            .eq('auslage', true)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

        if (cancelled) return;
        setLinked((akteResult.data as LinkedWerkstattAkte | null) ?? null);

        const rechnung = rechnungResult.data as {
          netto_betrag: number | null;
          abrechnungsschreiben_dokument_id: string | null;
        } | null;

        if (!rechnung) {
          setRechnungData(null);
          return;
        }

        let abrechnungsschreibenUrl: string | null = null;
        if (rechnung.abrechnungsschreiben_dokument_id) {
          const { data: dok } = await supabase
            .from('dokumente')
            .select('storage_pfad')
            .eq('id', rechnung.abrechnungsschreiben_dokument_id)
            .maybeSingle();
          const storagePfad = (dok as { storage_pfad: string | null } | null)?.storage_pfad ?? null;
          if (!cancelled && storagePfad) {
            const { data: signed } = await supabase.storage
              .from('dokumente')
              .createSignedUrl(storagePfad, 3600);
            abrechnungsschreibenUrl = signed?.signedUrl ?? null;
          }
        }

        if (!cancelled) {
          setRechnungData({
            auslage_netto: rechnung.netto_betrag,
            abrechnungsschreiben_dokument_id: rechnung.abrechnungsschreiben_dokument_id,
            abrechnungsschreiben_url: abrechnungsschreibenUrl,
          });
        }
      } catch {
        if (!cancelled) {
          setLinked(null);
          setRechnungData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [werkstattAkteId]);

  const kandidaten = (werkstattAutoMatchKandidaten ?? []).filter(Boolean);
  const hasKandidaten = kandidaten.length > 0;
  const hasLink = !!werkstattAkteId;
  const hasUnlinkedCallbacks = !!(onLinkExisting || onCreateNew);

  // Nothing to display in any mode — render null so the layout stays clean.
  if (!hasLink && !hasKandidaten && !hasUnlinkedCallbacks) return null;

  const aufschluesselung = hasLink
    ? berechneErstattungsAufschluesselung(
        gutachtenHonorarNetto,
        rechnungData?.auslage_netto,
      )
    : null;

  return (
    <div className="space-y-2">
      {/* ── Mode 1: LINKED — header chip with deep link ── */}
      {hasLink && (
        <LinkedChip
          linked={linked}
          loading={loading}
          auslageNetto={rechnungData?.auslage_netto ?? null}
          onNavigate={navigate}
        />
      )}

      {/* ── Demontage-Aufwand: Erstattungs-Aufschlüsselung (Issue #584) ── */}
      {hasLink && aufschluesselung && (
        <div
          data-testid="erstattungs-aufschluesselung"
          className="flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-lg border border-green-200 bg-green-50 text-[12px] text-green-900"
        >
          <Receipt className="w-3.5 h-3.5 flex-shrink-0 text-green-600" />
          <span>{formatErstattungsAufschluesselung(aufschluesselung)}</span>
        </div>
      )}

      {/* ── Abrechnungsschreiben-Link (aus Werkstatt-Akte, Issue #584) ── */}
      {hasLink && rechnungData?.abrechnungsschreiben_url && (
        <a
          data-testid="abrechnungsschreiben-link"
          href={rechnungData.abrechnungsschreiben_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" />
          Abrechnungsschreiben
          <ExternalLink className="w-3 h-3 opacity-60" />
        </a>
      )}

      {/* ── Mode 2: SUGGESTION — banner per auto-match candidate ── */}
      {hasKandidaten && (
        <div
          data-testid="werkstatt-auto-match-banner"
          className="flex flex-col gap-2 px-3 sm:px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50"
        >
          <div className="flex items-center gap-2 text-[12px] font-semibold text-amber-900">
            <Wrench className="w-3.5 h-3.5" />
            Mögliche Werkstatt-Akte gefunden
          </div>
          <ul className="flex flex-col gap-1.5">
            {kandidaten.map((k) => {
              const ident = aktenIdentifier(k);
              const summary = [k.kunde_name, k.kennzeichen]
                .filter(Boolean)
                .join(' · ');
              return (
                <li
                  key={k.id}
                  className="flex items-center gap-2 flex-wrap text-[12px] text-amber-900"
                >
                  <span className="font-mono font-semibold">
                    {ident ?? '—'}
                  </span>
                  {summary && <span className="opacity-80">· {summary}</span>}
                  {typeof k.score === 'number' && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                      {Math.round(k.score)}%
                    </span>
                  )}
                  {onConfirmKandidat && (
                    <button
                      type="button"
                      data-testid="werkstatt-kandidat-verknuepfen"
                      onClick={() => onConfirmKandidat(k)}
                      className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                    >
                      <Link2 className="w-3 h-3" />
                      Verknüpfen
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Mode 3: UNLINKED — action buttons ── */}
      {!hasLink && hasUnlinkedCallbacks && (
        <div className="flex flex-wrap items-center gap-2">
          {onLinkExisting && (
            <button
              type="button"
              data-testid="werkstatt-link-existing"
              onClick={onLinkExisting}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Mit bestehender Werkstatt-Akte verknüpfen
            </button>
          )}
          {onCreateNew && (
            <button
              type="button"
              data-testid="werkstatt-create-new"
              onClick={onCreateNew}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Neue Werkstatt-Akte anlegen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LinkedChip({
  linked,
  loading,
  auslageNetto,
  onNavigate,
}: {
  linked: LinkedWerkstattAkte | null;
  loading: boolean;
  auslageNetto: number | null;
  onNavigate: (href: string) => void;
}) {
  if (loading && !linked) {
    return (
      <div
        data-testid="werkstatt-chip-loading"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-[12px] text-gray-500"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Werkstatt-Akte wird geladen...
      </div>
    );
  }
  if (!linked) return null;

  const identifier = aktenIdentifier(linked);
  if (!identifier) return null;

  const href = buildAkteHref('werkstatt', identifier);
  const summary = [linked.kunde_name, linked.kennzeichen]
    .filter(Boolean)
    .join(' · ');

  return (
    <a
      data-testid="werkstatt-chip-link"
      href={href}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(href);
      }}
      className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 transition-colors group max-w-full"
    >
      <span className="w-6 h-6 rounded-md bg-red-600 text-white flex items-center justify-center flex-shrink-0">
        <Wrench className="w-3.5 h-3.5" />
      </span>
      <span className="flex items-baseline gap-1.5 min-w-0">
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
          Werkstatt:
        </span>
        <span className="text-[12px] font-semibold">LCS</span>
        <span className="text-[12px] opacity-60">/</span>
        <span className="text-[12px] font-mono font-semibold truncate">
          {identifier}
        </span>
        {summary && (
          <span className="text-[11px] opacity-70 truncate hidden sm:inline">
            · {summary}
          </span>
        )}
      </span>
      {auslageNetto != null && (
        <span
          data-testid="werkstatt-auslage-betrag"
          className="ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 flex-shrink-0"
        >
          Auslage: {auslageNetto.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
        </span>
      )}
      <ExternalLink className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 flex-shrink-0" />
    </a>
  );
}
